import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { StoreThemeTokens } from "./themes/storeThemes";

interface BrandNoteProps {
  noteData?: {
    show_banner?: boolean;
    title?: string;
    subtitle?: string;
    script_text?: string;
  };
  storeName?: string;
  themeTokens: StoreThemeTokens;
}

const BrandNote: React.FC<BrandNoteProps> = ({
  noteData,
  storeName,
  themeTokens,
}) => {
  const show =
    noteData?.show_banner !== undefined ? Boolean(noteData.show_banner) : true;

  if (!show) return null;

  const title =
    noteData?.title || "🤎 Thank you for supporting our small business!";
  const subtitle =
    noteData?.subtitle || `${storeName || "RUDRIX"} | Elegance Meets Care`;
  const scriptText = noteData?.script_text || "Self Care Brighter You ♡";

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 7 },
        background: themeTokens.brandNoteGradient,
        borderTop: `1px solid ${themeTokens.brandNoteBorder}`,
        borderBottom: `1px solid ${themeTokens.brandNoteBorder}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Botanical Leaf (Left) */}
      <Box
        sx={{
          position: "absolute",
          left: -30,
          bottom: -30,
          width: 160,
          height: 160,
          color: themeTokens.accent,
          opacity: 0.18,
          pointerEvents: "none",
        }}
      >
        <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 100 100" width="100%" height="100%">
          <path d="M10,90 Q50,50 90,10 Q50,50 10,90 Z" />
          <path d="M30,70 Q70,30 90,10" />
          <path d="M10,90 Q20,30 50,20" />
        </svg>
      </Box>

      {/* Decorative Botanical Leaf (Right) */}
      <Box
        sx={{
          position: "absolute",
          right: -30,
          top: -30,
          width: 180,
          height: 180,
          color: themeTokens.accent,
          opacity: 0.18,
          pointerEvents: "none",
        }}
      >
        <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 100 100" width="100%" height="100%">
          <path d="M10,90 Q50,50 90,10 Q50,50 10,90 Z" />
          <path d="M30,70 Q70,30 90,10" />
        </svg>
      </Box>

      <Container maxWidth="md" sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: themeTokens.fontSerif,
            fontSize: { xs: "1.25rem", sm: "1.6rem", md: "1.85rem" },
            fontWeight: 600,
            color: themeTokens.textPrimary,
            letterSpacing: "0.02em",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: { xs: "0.75rem", sm: "0.82rem" },
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: themeTokens.textSecondary,
            mb: 2,
          }}
        >
          {subtitle}
        </Typography>

        {scriptText && (
          <Typography
            variant="h4"
            sx={{
              fontFamily: themeTokens.fontScript,
              fontSize: { xs: "1.8rem", sm: "2.3rem", md: "2.6rem" },
              color: themeTokens.accent,
              fontWeight: 700,
              letterSpacing: "0.02em",
              mt: 1,
            }}
          >
            {scriptText}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default BrandNote;
